import { ClientUser, UserRole } from '@numeo/types-core'
import { TeamDriverRow } from '@numeo/types-main-web-app'
import { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppStore } from '@/hooks'
import { selectTeamDirectoryEnabled } from '@/hooks/team/useTeamDirectoryEnabled'
import { TeamInvalidation, useInvalidateTeam } from '@/hooks/team/useInvalidateTeam'
import { TEAM_LIST_ID, teamApi } from '@/store/api/teamApi'
import { listDriversFromUsers, TEAM_MAX_IDS } from '@/utils/team/teamQuery'

/** The most drivers one `view=full` read may name (server limit). */
const MAX_FULL_IDS = 20

const chunk = (ids: readonly string[], size: number): string[][] => {
    const unique = [...new Set(ids.filter(Boolean).map(String))].sort()
    const chunks: string[][] = []
    for (let start = 0; start < unique.length; start += size) chunks.push(unique.slice(start, start + size))
    return chunks
}

interface DriverDirectoryActions {
    /** The whole stored record of these drivers (the edit form's), read now: `view=full` on the directory, else the roster. */
    loadFullDrivers: (ids: readonly string[]) => Promise<ClientUser[]>
    /** The directory rows of these drivers (status, truck, co-driver), read now. */
    loadDriverRows: (ids: readonly string[]) => Promise<TeamDriverRow[]>
    /** After MY driver save: every driver list is fetched once more (see `useInvalidateTeam`). */
    invalidateDrivers: (target?: Omit<TeamInvalidation, 'drivers' | 'members'>) => void
}

/**
 * The imperative side of the drivers directory, for code that needs a driver at click or save time rather than at
 * render: opening the edit form, the co-driver a save rewrites, the seats of the truck a save changes.
 *
 * Each read goes through the same RTK Query cache the lists use (a record read in the last five minutes is not asked
 * again) and drops its subscription when it has answered. Before the directory serves the company, the roster in the
 * store answers instead, as it always has.
 */
export const useDriverDirectoryActions = (): DriverDirectoryActions => {
    const dispatch = useAppDispatch()
    const store = useAppStore()
    const invalidateTeam = useInvalidateTeam()

    const loadFullDrivers = useCallback(
        async (ids: readonly string[]): Promise<ClientUser[]> => {
            const state = store.getState()
            const applicationId = state.auth.application?.id ? String(state.auth.application.id) : undefined
            if (!selectTeamDirectoryEnabled(state) || !applicationId) {
                const wanted = new Set(ids.map(String))
                // The scoped roster (what the Drivers list rendered), overridden by the application's roster, which a save
                // that answered with the application has just refreshed.
                const roster = applicationId ? teamApi.endpoints.getScopedDriverRoster.select({ applicationId })(state).data : undefined
                const found = new Map<string, ClientUser>()
                for (const user of [...(roster ?? []), ...(state.auth.application?.users ?? [])])
                    if (wanted.has(String(user.id))) found.set(String(user.id), user)
                return [...found.values()].filter((user) => user.role === UserRole.DRIVER)
            }
            const results = await Promise.all(
                chunk(ids, MAX_FULL_IDS).map(async (part) => {
                    const subscription = dispatch(teamApi.endpoints.getTeamDriversFull.initiate({ applicationId, ids: part }))
                    try {
                        return await subscription.unwrap()
                    } finally {
                        subscription.unsubscribe()
                    }
                })
            )
            return results.flat()
        },
        [dispatch, store]
    )

    const loadDriverRows = useCallback(
        async (ids: readonly string[]): Promise<TeamDriverRow[]> => {
            const state = store.getState()
            const applicationId = state.auth.application?.id ? String(state.auth.application.id) : undefined
            const wanted = chunk(ids, TEAM_MAX_IDS)
            if (!wanted.length) return []
            if (!selectTeamDirectoryEnabled(state) || !applicationId) return listDriversFromUsers(state.auth.application?.users, { ids: wanted.flat() }).items
            const pages = await Promise.all(
                wanted.map(async (part) => {
                    const subscription = dispatch(teamApi.endpoints.listTeamDrivers.initiate({ applicationId, ids: part }))
                    try {
                        return (await subscription.unwrap()).items
                    } finally {
                        subscription.unsubscribe()
                    }
                })
            )
            return pages.flat()
        },
        [dispatch, store]
    )

    const invalidateDrivers = useCallback(
        (target: Omit<TeamInvalidation, 'drivers' | 'members'> = {}) => {
            invalidateTeam({ ...target, drivers: true })
            // Before the directory serves the company, the Profiles lists read the Groups-scoped roster, which the
            // same tag holds; `useInvalidateTeam` leaves the cache alone at that stage.
            if (!selectTeamDirectoryEnabled(store.getState())) dispatch(teamApi.util.invalidateTags([{ type: 'TeamDrivers', id: TEAM_LIST_ID }]))
        },
        [dispatch, invalidateTeam, store]
    )

    return useMemo(() => ({ loadFullDrivers, loadDriverRows, invalidateDrivers }), [loadFullDrivers, loadDriverRows, invalidateDrivers])
}
